import { PricingContextProvider } from '@vp/react-pricing-context';
import { Box, ScreenClassProvider, SiteContent, SiteMain, SwanFontNameEnum } from '@vp/swan';
import { useLogger, useSwanFontKeys, useSwanStyleKeys, useUserContext } from '@vp/ubik-context';
import { FragmentLogger } from '@vp/ubik-logging';
import i18n, { Resource } from 'i18next';
import { Breadcrumbs, IdentityProvider } from '@99designs/design-services-common';
import {
    FeatureFlagProvider,
    VistaExperiment,
    VistaExperimentsProvider,
    featureFlagsFromUbikUserContext,
    vistaExperimentsFromUbikUserContext,
} from '@99designs/experiments';
import { ProductPricingProvider } from '@99designs/feature-design-services-landing';
import { AuthenticatedApolloProvider } from '@99designs/graph-utils';
import { getCountryCode } from '@99designs/i18n';
import { PAGE_TRACKING_CONFIG, PageProvider, QualarooLoader } from '@99designs/tracking';
import { Content } from '../components/content';
import { LOGGER_FRAGMENT_ID } from '../lib/consts';
import {
    EnvironmentConfig,
    EnvironmentConfigProvider,
} from '../lib/environment/environmentContext';
import type {
    PopularProductsType as PopularProducts,
    ProductGroups,
} from '@99designs/feature-design-services-landing';

export interface Props {
    localeReference: string;
    resources: Resource;
    locale: string;
    productGroups: ProductGroups;
    envConfig: EnvironmentConfig['public'];
    popularProducts: PopularProducts;
}

export const App = ({ resources, productGroups, envConfig, popularProducts }: Props) => {
    const logger = useLogger() as FragmentLogger;
    const userContext = useUserContext();
    logger.setContext({ fragmentId: LOGGER_FRAGMENT_ID });

    i18n.init({
        resources,
        debug: envConfig.enableTranslationDebug,
        lowerCaseLng: true, //locales are set to lower case e.g. "en-us" not "en-US"
        interpolation: {
            escapeValue: false, // React already provides escaping
        },
        nsSeparator: '*****', // namespace selector is a string delimited which defaults to ":". Overriding to *** so that urls don't get split
        returnObjects: true,
    });
    i18n.changeLanguage(userContext.locale);

    useSwanStyleKeys([
        'anchorBar',
        'accordion',
        'alertBox',
        'avatar',
        'banner',
        'button',
        'carousel',
        'core',
        'grid',
        'hidden',
        'icon',
        'listbox',
        'modalDialog',
        'tabs',
        'thumbnailsHero',
        'pagination',
        'popover',
        'progressiveImage',
        'selectionSet',
        'spinner',
        'standardTile',
        'toggleSwitch',
        'utility',
        'visible',
        'zoomControls',
        'fileDropzone',
        'fileTile',
        'table',
    ]);

    useSwanFontKeys([
        SwanFontNameEnum.Graphik,
        SwanFontNameEnum.GraphikRegular,
        SwanFontNameEnum.GraphikMedium,
        SwanFontNameEnum.Tiempos,
    ]);

    const experiments: VistaExperiment[] = userContext?.experiments
        ? vistaExperimentsFromUbikUserContext(userContext?.experiments, logger)
        : [];

    const pageTracking = {
        ...PAGE_TRACKING_CONFIG['discover'],
        pageName: 'Design Services:Hub Page',
        pagePath: '/design',
    };

    const featureFlags = featureFlagsFromUbikUserContext(userContext?.featureFlags);
    const locale = userContext.locale;
    const environment = userContext.environment || 'production';

    const getProductsFromProductGroups = (productGroups: ProductGroups) => {
        return productGroups.flatMap((group) => group.products);
    };
    const products = [...getProductsFromProductGroups(productGroups), ...(popularProducts || [])];

    return (
        <EnvironmentConfigProvider value={envConfig}>
            <VistaExperimentsProvider value={experiments}>
                <FeatureFlagProvider value={featureFlags}>
                    <IdentityProvider locale={locale} developmentMode={environment === 'dev'}>
                        <PricingContextProvider
                            initializationConfig={{
                                merchantId: 'vistaprint',
                                market: getCountryCode(locale),
                                isDevelopmentMode: environment === 'development',
                            }}
                        >
                            <AuthenticatedApolloProvider
                                locale={locale}
                                environment={environment}
                                endpoint={
                                    environment === 'development'
                                        ? 'http://graph.dockervm:49944/graph'
                                        : 'https://graph.es.vpsvc.com/graph'
                                }
                                hideLoader={true}
                            >
                                <ScreenClassProvider>
                                    <PageProvider locale={locale} pageData={pageTracking}>
                                        <ProductPricingProvider products={products}>
                                            <Breadcrumbs crumbs={[]} />
                                            <Box as="main" id="maincontentstart">
                                                <SiteContent>
                                                    <SiteMain>
                                                        <Content
                                                            productGroups={productGroups}
                                                            popularProducts={popularProducts}
                                                        />
                                                    </SiteMain>
                                                </SiteContent>
                                            </Box>
                                        </ProductPricingProvider>
                                    </PageProvider>
                                </ScreenClassProvider>
                                <QualarooLoader />
                            </AuthenticatedApolloProvider>
                        </PricingContextProvider>
                    </IdentityProvider>
                </FeatureFlagProvider>
            </VistaExperimentsProvider>
        </EnvironmentConfigProvider>
    );
};
